import { QueryClient } from '@tanstack/react-query'
import { INotificationContentHandler } from '@/lib/handler/notification-content-handler'
import { NotificationType } from '@/types/notification'

/**
 * Locale Content Handler
 * Updates cache when a locale is created, updated, or deleted
 */
export default class LocaleContentHandler implements INotificationContentHandler {
  async handle(notification: NotificationType, queryClient: QueryClient): Promise<void> {
    const { payload } = notification
    const action = payload?.action

    // Handle different actions
    switch (action) {
      case 'CREATE':
        await this.handleCreate(payload, queryClient)
        break
      case 'UPDATE':
        await this.handleUpdate(payload, queryClient)
        break
      case 'DELETE':
        await this.handleDelete(payload, queryClient)
        break
      default:
        // If no action specified, just invalidate everything
        await this.invalidateAll(queryClient)
    }

    // Invalidate meta to update available locales
    await queryClient.invalidateQueries({
      queryKey: ['meta']
    })
    await queryClient.refetchQueries({
      queryKey: ['meta'],
      type: 'active'
    })
  }

  private async handleCreate(_payload: any, queryClient: QueryClient): Promise<void> {
    // Invalidate and refetch locales list
    await queryClient.invalidateQueries({
      queryKey: ['locales']
    })
    await queryClient.refetchQueries({
      queryKey: ['locales'],
      type: 'active'
    })
  }

  private async handleUpdate(payload: any, queryClient: QueryClient): Promise<void> {
    const localeId = payload?.id

    // Invalidate and refetch locales list
    await queryClient.invalidateQueries({
      queryKey: ['locales']
    })
    await queryClient.refetchQueries({
      queryKey: ['locales'],
      type: 'active'
    })

    // If we have an id, also invalidate and refetch locale detail
    if (localeId) {
      await queryClient.invalidateQueries({
        queryKey: ['locale', localeId]
      })
      await queryClient.refetchQueries({
        queryKey: ['locale', localeId],
        type: 'active'
      })
    }
  }

  private async handleDelete(payload: any, queryClient: QueryClient): Promise<void> {
    const localeCode = payload?.localeCode

    // If we have the locale code, try to remove it from cache
    if (localeCode) {
      const listQueries = queryClient.getQueriesData({ queryKey: ['locales'] })

      listQueries.forEach(([queryKey, oldData]: [any, any]) => {
        if (!oldData || !oldData.items) {
          return
        }

        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: oldData.items.filter((item: any) => item.fullCode !== localeCode),
          total: oldData.total - 1
        })
      })
    }

    // Invalidate and refetch locales list to ensure consistency
    await queryClient.invalidateQueries({
      queryKey: ['locales']
    })
    await queryClient.refetchQueries({
      queryKey: ['locales'],
      type: 'active'
    })
  }

  private async invalidateAll(queryClient: QueryClient): Promise<void> {
    // Invalidate and refetch everything locale-related
    await queryClient.invalidateQueries({
      queryKey: ['locales']
    })
    await queryClient.refetchQueries({
      queryKey: ['locales'],
      type: 'active'
    })
  }
}
